import React, { useState, useEffect } from 'react';
import ShipCard from '../Ship/ShipCard';
import { MiningZoneConstants } from '../../constants/miningZones';
import ShipPurchasePanel from '../Ship/ShipPurchasePanel';
import { useGameStateContext } from '../../state/GameStateContext';
import FancyButton from '../FancyButton';
import '../FancyButton.css';
import './FleetPanel.css';
import type { ShipDto, ShipPurchaseOption } from '../../types/ship-types';
import { ShipCategory } from '../../types/ship-types';
import { usePrestige } from '../../state/usePrestige';
import PrestigeModal from '../Prestige/PrestigeModal';
import MiningZoneSelector from '../MiningZoneSelector/MiningZoneSelector';
import { debugLog } from '../../utils/debugLogger';
// Images
import commandShipIcon from '../../assets/ships/command-ship.png';
import haulerShipIcon from '../../assets/ships/hauler-ship.png';
import miningFrigateIcon from '../../assets/ships/mining-frigate.png';
import freighterIcon from '../../assets/ships/freighter.png';
import bargeIcon from '../../assets/ships/barge.png';

type FleetPanelProps = {
  fleet: ShipDto[];
  playerId: string;
  reload: (showLoader?: boolean) => void;
  onWarpAll: () => void;
  onRecallAll: () => void;
  onWarpShip: (shipId: string) => void;
  onRecallShip: (shipId: string) => void;
  onPurchaseUpgrade: (shipId: string, branchType: string | number, upgradeName: string) => void;
  basicOre: number;
};

const FleetPanel: React.FC<FleetPanelProps> = ({
  fleet,
  playerId,
  reload,
  onWarpAll,
  onRecallAll,
  onWarpShip,
  onRecallShip,
  onPurchaseUpgrade,
  basicOre
}) => {
  const { playerName: statePlayerName, gameState, loadGameState, doBuildShip, doChangeMiningZone } = useGameStateContext();
  const {
    prestigeStatus,
    availableUpgrades,
    selectedUpgrade,
    setSelectedUpgrade,
    loading: prestigeLoading,
    showPrestigeModal,
    setShowPrestigeModal,
    doPrestige
  } = usePrestige(playerId, gameState, loadGameState);
  // Track expanded categories
  const [expandedCategories, setExpandedCategories] = useState<{ [key: number]: boolean }>({
    [ShipCategory.Mining]: true,
    [ShipCategory.Command]: true,
    [ShipCategory.Hauler]: true,
    [ShipCategory.Support]: true,
    [ShipCategory.Mothership]: true
  });

  // Add state for purchase panel collapse
  const [purchasePanelExpanded, setPurchasePanelExpanded] = useState(true);

  // Ship purchase options are now part of the game state
  const [shipPurchaseOptions, setShipPurchaseOptions] = React.useState<ShipPurchaseOption[]>([]);
  const [apiError, setApiError] = useState(false);

  // Add loading state for ship purchase options
  const [shipPurchaseLoading, setShipPurchaseLoading] = useState(true);

  React.useEffect(() => {
    if (gameState) {
      setShipPurchaseLoading(true);
      const options = gameState.shipPurchaseOptions || [];
      setShipPurchaseOptions(options);
      setApiError(false);
      setShipPurchaseLoading(false);
    }
  }, [gameState]);

  // Unified purchase handler
  const handlePurchaseShip = async (type: string | number) => {
    const normalizedType = type?.toString().toLowerCase();
    if (normalizedType?.includes('mining')) {
      debugLog(`[ShipPurchase] Mining ship buy clicked (type=${type}) | playerId=${playerId} | ore=${basicOre}`);
      console.log('[ShipPurchase] Mining ship buy clicked', { type, playerId, ore: basicOre });
    } else {
      debugLog(`[ShipPurchase] Buy clicked (type=${type}) | playerId=${playerId} | ore=${basicOre}`);
      console.log('[ShipPurchase] Buy clicked', { type, playerId, ore: basicOre });
    }
    // Server rejections are reported by the game-state hook via errorService.
    await doBuildShip(type);
  };
  // Helper: does the player have any mining ships?
  const hasMiningShip = fleet.some(ship => ship.category === ShipCategory.Mining);

  // Always expand the purchase panel for fresh players (no mining ships yet)
  useEffect(() => {
    if (!hasMiningShip) {
      setPurchasePanelExpanded(true);
    }
  }, [hasMiningShip]);

  // Toggle category expansion
  const toggleCategory = (category: number) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  // Get category name for display
  const getCategoryName = (category: number): string => {
    const categoryNames: Record<number, string> = {
      [ShipCategory.Mining]: "Mining Ships",
      [ShipCategory.Hauler]: "Hauler Ships",
      [ShipCategory.Command]: "Command Ships",
      [ShipCategory.Support]: "Support Ships",
      [ShipCategory.Mothership]: "Motherships"
    };
    return categoryNames[category] || "Other Ships";
  };

  // Helper to map ship type to icon and base stats (example, adjust as needed)
  const getShipIcon = (type: string) => {
    switch (type) {
      case 'command-ship': return commandShipIcon;
      case 'hauler-ship': return haulerShipIcon;
      case 'mining-frigate': return miningFrigateIcon;
      case 'freighter': return freighterIcon;
      case 'barge': return bargeIcon;
      default: return '';
    }
  };  // Cache of known ship stats to avoid searching through fleet repeatedly
  const [shipStatsCache, setShipStatsCache] = useState<Record<string, any>>({});

  // Update the ship stats cache when the fleet changes
  React.useEffect(() => {
    if (fleet.length === 0) return;

    // Build a cache of ship stats by type and name for quick lookup
    const newCache: Record<string, any> = {};
    fleet.forEach(ship => {
      // Create the stats object once for reuse
      const shipStats = {
        mining: ship.baseMiningSpeed,
        cargo: ship.baseCargoCapacity,
        speed: ship.baseWarpSpeed,
        warpSpeed: ship.baseWarpSpeed,
        unloadSpeed: ship.baseUnloadSpeed,
        // Add any special properties based on ship type
        fleetBonus: ship.type.toString().toLowerCase() === 'command' ? 0.10 : 0 // 10% fleet bonus for command ships
      };

      // Store by type (lowercase for case-insensitive matching)
      const typeKey = ship.type.toString().toLowerCase();
      if (!newCache[typeKey]) {
        newCache[typeKey] = shipStats;
      }

      // Also store by name for fuzzy matching
      const nameKey = ship.name.toLowerCase();
      if (!newCache[nameKey]) {
        newCache[nameKey] = shipStats;
      }
      // Store by category for broader matching
      const categoryKey = getCategoryName(ship.category).toLowerCase().replace(/\s+ships$/, '');
      if (!newCache[categoryKey]) {
        newCache[categoryKey] = shipStats;
      }
    });

    setShipStatsCache(newCache);
  }, [fleet]);

  // Map ship purchase options to base stats for display in the popover
  // Uses actual data from fleet when available via the cache
  const getBaseStats = (option: ShipPurchaseOption) => {
    const type = option.type.toLowerCase();
    const name = option.name.toLowerCase();

    // Check cache first for direct match
    if (shipStatsCache[type]) {
      return shipStatsCache[type];
    }

    // Try name match in cache
    if (shipStatsCache[name]) {
      return shipStatsCache[name];
    }

    // Try partial matches in cache
    for (const key of Object.keys(shipStatsCache)) {
      if (type.includes(key) || key.includes(type)) {
        return shipStatsCache[key];
      }
    }

    // Try to find matching ship in fleet as fallback (in case cache isn't built yet)
    const matchingShip = fleet.find(ship =>
      ship.type.toString().toLowerCase() === type ||
      ship.name.toLowerCase().includes(type) ||
      type.includes(ship.type.toString().toLowerCase())
    );

    if (matchingShip) {
      // Use actual values from the fleet data
      return {
        mining: matchingShip.baseMiningSpeed,
        cargo: matchingShip.baseCargoCapacity,
        speed: matchingShip.baseWarpSpeed,
        warpSpeed: matchingShip.baseWarpSpeed,
        unloadSpeed: matchingShip.baseUnloadSpeed
      };
    }

    // Default stats if no match
    return {
      mining: 0,
      cargo: 0,
      speed: 0,
      warpSpeed: 0,
      unloadSpeed: 0,
      fleetBonus: 0
    };
  };

  // Add chevron SVG component
  const ChevronIcon: React.FC<{ expanded: boolean }> = ({ expanded }) => (
    <span
      className={"expand-icon" + (expanded ? " expanded" : "")}
      style={{ display: 'inline-block', transition: 'transform 0.3s', marginLeft: 8, transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
      aria-label={expanded ? 'Collapse' : 'Expand'}
    >
      {/* SVG chevron down */}
      <svg width="18" height="18" viewBox="0 0 20 20">
        <polyline points="6 8 10 12 14 8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );

  // State for mining zone selection
  const [currentMiningZoneId, setCurrentMiningZoneId] = useState<string>(MiningZoneConstants.ORE_BELT);
  const [zoneChangeError, setZoneChangeError] = useState<string | null>(null);

  // Fetch mining zones when component mounts
  // Mining zones are now fetched directly by MiningZoneSelector component

  // Update current mining zone ID from game state
  useEffect(() => {
    if (gameState) {
      // Get the zone ID from the activeMiningZone object
      const activeZoneId = gameState.activeMiningZone?.id || MiningZoneConstants.ORE_BELT;

      setCurrentMiningZoneId(activeZoneId);
    }
  }, [gameState]);

  // Handle mining zone change
  const handleMiningZoneChange = async (zoneId: string) => {
    if (zoneId === currentMiningZoneId) return;

    setZoneChangeError(null);

    try {
      await doChangeMiningZone(zoneId);
      setCurrentMiningZoneId(zoneId);
      // Reload game state to reflect the change
      reload();
    } catch (err: any) {
      console.error('Error changing mining zone:', err);
      setZoneChangeError(err?.message || 'Failed to change mining zone');
    }
  };

  if (apiError) {
    return (
      <div className="panel fleet-panel">
        <div className="empty-fleet-container">
          <div className="empty-fleet-message">
            <h3>Unable to Connect</h3>
            <p>There was a problem connecting to the game server.
              <br />
              Jamie is probably aware, but please check your connection and try again.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="panel fleet-panel">
      <div className="panel-header">
        <h2>{statePlayerName ? `${statePlayerName}'s Fleet` : 'Your Fleet'}</h2>
        {/* Prestige Button in the middle - prestigeStatus is now from gameState via usePrestige hook */}
        {prestigeStatus?.canPrestige && (
          <span style={{ display: 'flex', alignItems: 'center' }} title="Prestige Available!">
            <FancyButton
              variant="yellow"
              className="prestige-btn"
              style={{ margin: '0 1rem', display: 'flex', alignItems: 'center', gap: 8 }}
              onClick={() => setShowPrestigeModal(true)}
            >
              Prestige
            </FancyButton>
          </span>
        )}
        {showPrestigeModal && (
          <PrestigeModal
            currentPrestigeScore={prestigeStatus?.currentPrestigeScore || 0}
            threshold={prestigeStatus?.prestigeThreshold || 0}
            availableUpgrades={availableUpgrades}
            selectedUpgrade={selectedUpgrade}
            onSelectUpgrade={setSelectedUpgrade}
            onConfirm={doPrestige}
            onCancel={() => setShowPrestigeModal(false)}
            loading={prestigeLoading}
          />
        )}
        <div className="button-group">
          <FancyButton onClick={onWarpAll} variant="green">Warp All</FancyButton>
          <FancyButton onClick={onRecallAll} variant="red">Recall All</FancyButton>
        </div>
      </div>

      {/* Mining Zone and Purchase Ships Row */}
      <div className="ship-categories-row" style={{ display: 'flex', width: '100%', gap: '1rem', marginBottom: '1rem', alignItems: 'stretch' }}>
        {/* Mining Zone Selector */}
        <div className="ship-category-section size-small" style={{ flex: '1', minWidth: 0, display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
          <MiningZoneSelector
            currentZoneId={currentMiningZoneId}
            playerId={playerId}
            onZoneChange={handleMiningZoneChange}
          />
          {zoneChangeError && (
            <div className="zone-error-message">
              Error: {zoneChangeError}
            </div>
          )}
        </div>

        {/* Purchase Ships panel */}
        <div className="ship-category-section size-small" style={{ flex: '1', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div
            className="category-header"
            style={{ cursor: 'pointer', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            onClick={() => setPurchasePanelExpanded(e => !e)}
            tabIndex={0}
            role="button"
            aria-expanded={purchasePanelExpanded}
          >
            <h3 style={{ margin: 0 }}>Purchase Ships</h3>
            <ChevronIcon expanded={purchasePanelExpanded} />
          </div>
          {purchasePanelExpanded && (
            <div className="category-container" style={{ boxSizing: 'border-box' }}>
              {shipPurchaseLoading ? (
                <div className="mining-zone-selector loading">Loading ship purchase options...</div>
              ) : (
                <ShipPurchasePanel ships={shipPurchaseOptions.map(option => ({
                  type: option.type,
                  name: option.name,
                  description: option.description,
                  specificationDescription: option.specificationDescription,
                  specificationsHeader: option.specificationsHeader,
                  specificationNote: option.specificationNote,
                  cost: option.cost,
                  baseStats: getBaseStats(option),
                  onPurchase: () => handlePurchaseShip(option.originalType),
                  disabled: basicOre < option.cost,
                  playerOre: basicOre,
                  iconUrl: getShipIcon(option.type)
                }))} />
              )}
            </div>
          )}
        </div>
      </div>
      {!hasMiningShip ? (
        // When no ships, use a clean layout dedicated to just ship purchases
        <div className="empty-fleet-container">
          <div className="empty-fleet-message">
            <h3>Welcome Commander!</h3>
            <p>Purchase your first mining ship to begin your space mining operation.</p>
          </div>
        </div>
      ) : (
        <div className="ship-categories-rows">
          {/* Render small categories (≤4 ships) in rows, never splitting a category, and mixing categories up to 4 ships per row */}
          {(() => {
            // Build rows: mix small categories up to 4 ships per row, never split a category unless >4
            const categories = [
              ShipCategory.Mining,
              ShipCategory.Hauler,
              ShipCategory.Command,
              ShipCategory.Support,
              ShipCategory.Mothership
            ];
            const rows: Array<Array<{ category: number; ships: ShipDto[] }>> = [];
            let currentRow: Array<{ category: number; ships: ShipDto[] }> = [];
            let currentCount = 0;
            for (const category of categories) {
              const ships = fleet.filter(ship => ship.category === category);
              if (ships.length === 0) continue;
              if (ships.length > 4) {
                // Large category: single panel, grid layout
                rows.push([{ category, ships }]);
              } else {
                if (currentCount + ships.length > 4) {
                  if (currentRow.length > 0) rows.push(currentRow);
                  currentRow = [];
                  currentCount = 0;
                }
                currentRow.push({ category, ships });
                currentCount += ships.length;
              }
            }
            if (currentRow.length > 0) rows.push(currentRow);
            return rows.map((row, rowIdx) => {
              // If this is a large category (only one in the row and >4 ships), render as a grid panel
              if (row.length === 1 && row[0].ships.length > 4) {
                const { category, ships } = row[0];
                return (
                  <div key={category} className="ship-category-section size-full">
                    <div
                      className="category-header"
                      style={{ cursor: 'pointer', boxSizing: 'border-box' }}
                      onClick={() => toggleCategory(category)}
                      tabIndex={0}
                      role="button"
                      aria-expanded={!!expandedCategories[category]}
                    >
                      <h3 style={{ display: 'flex', alignItems: 'center' }}>
                        {getCategoryName(category)}
                        <span className="category-ships-count">({ships.length})</span>
                      </h3>
                      <ChevronIcon expanded={!!expandedCategories[category]} />
                    </div>
                    {expandedCategories[category] && (
                      <div className="category-container" style={{ paddingLeft: '0.5rem', paddingRight: '0.5rem', boxSizing: 'border-box' }}>
                        <div className="ships-in-category" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', justifyItems: 'start' }}>
                          {ships.map(ship => (
                            <ShipCard
                              key={ship.id}
                              ship={ship}
                              onWarp={() => onWarpShip(ship.id)}
                              onRecall={() => onRecallShip(ship.id)}
                              onPurchaseUpgrade={(branchType: string, upgradeName: string) => onPurchaseUpgrade(ship.id, branchType, upgradeName)}
                              playerOre={basicOre}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              // If this is a single small category (≤4 ships), make it take 100% width
              if (row.length === 1 && row[0].ships.length <= 4) {
                const { category, ships } = row[0];
                return (
                  <div className="ship-categories-row" key={rowIdx} style={{ display: 'flex', width: '100%', gap: '0.5rem' }}>
                    <div
                      key={category + '-' + rowIdx}
                      className="ship-category-section size-small"
                      style={{ flex: '1 1 100%' }}
                    >
                      <div
                        className="category-header"
                        style={{ cursor: 'pointer', boxSizing: 'border-box' }}
                        onClick={() => toggleCategory(category)}
                        tabIndex={0}
                        role="button"
                        aria-expanded={!!expandedCategories[category]}
                      >
                        <h3 style={{ display: 'flex', alignItems: 'center' }}>
                          {getCategoryName(category)}
                          <span className="category-ships-count">({ships.length})</span>
                        </h3>
                        <ChevronIcon expanded={!!expandedCategories[category]} />
                      </div>
                      {expandedCategories[category] && (
                        <div className="category-container" style={{ paddingLeft: '0.5rem', paddingRight: '0.5rem', boxSizing: 'border-box' }}>
                          <div className="ships-in-category" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', justifyItems: 'center' }}>
                            {ships.map(ship => (
                              <ShipCard
                                key={ship.id}
                                ship={ship}
                                onWarp={() => onWarpShip(ship.id)}
                                onRecall={() => onRecallShip(ship.id)}
                                onPurchaseUpgrade={(branchType: string, upgradeName: string) => onPurchaseUpgrade(ship.id, branchType, upgradeName)}
                                playerOre={basicOre}
                              />
                            ))}
                            {/* Fill out empty grid cells for even spacing */}
                            {Array.from({ length: 4 - ships.length }).map((_, idx) => (
                              <div key={`placeholder-${idx}`} className="ship-card-placeholder" />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              }
              // Otherwise, render a row with mixed small categories, each category gets a .ship-category-section with correct label and container
              const totalShips = row.reduce((sum, cat) => sum + cat.ships.length, 0);
              return (
                <div className="ship-categories-row" style={{ display: 'flex', width: '100%', gap: '0.5rem', marginBottom: '0.5rem' }} key={rowIdx}>
                  {row.map(({ category, ships }, idx) => (
                    <div
                      key={category + '-' + rowIdx + '-' + idx}
                      className="ship-category-section size-small"
                      style={{ flex: `${ships.length} 0 0` }}
                    >
                      <div
                        className="category-header"
                        style={{ cursor: 'pointer', boxSizing: 'border-box' }}
                        onClick={() => toggleCategory(category)}
                        tabIndex={0}
                        role="button"
                        aria-expanded={!!expandedCategories[category]}
                      >
                        <h3 style={{ display: 'flex', alignItems: 'center' }}>
                          {getCategoryName(category)}
                          <span className="category-ships-count">({ships.length})</span>
                        </h3>
                        <ChevronIcon expanded={!!expandedCategories[category]} />
                      </div>
                      {expandedCategories[category] && (
                        <div className="category-container" style={{ paddingLeft: '0.5rem', paddingRight: '0.5rem', boxSizing: 'border-box' }}>
                          <div className="ships-in-category" style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem' }}>
                            {ships.map(ship => (
                              <ShipCard
                                key={ship.id}
                                ship={ship}
                                onWarp={() => onWarpShip(ship.id)}
                                onRecall={() => onRecallShip(ship.id)}
                                onPurchaseUpgrade={(branchType: string, upgradeName: string) => onPurchaseUpgrade(ship.id, branchType, upgradeName)}
                                playerOre={basicOre}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {/* Fill out empty cells if less than 4 ships for even spacing */}
                  {Array.from({ length: 4 - totalShips }).map((_, idx) => (
                    <div key={`placeholder-${idx}`} className="ship-card-placeholder" style={{ flex: '1 0 0' }} />
                  ))}
                </div>
              );
            });
          })()}
        </div>
      )}
    </div>
  );
};

export default FleetPanel;
